import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Store, ShieldCheck, Megaphone, TrendingUp, DollarSign,
  Package, ChevronRight, Users, Truck, CreditCard, Zap,
} from 'lucide-react';

const STEPS = [
  { icon: Store, title: 'Crea tu tienda', desc: 'Registrate gratis, configura tu perfil y empieza a vender en minutos.' },
  { icon: Package, title: 'Publica tus productos', desc: 'Sube fotos, pon tu precio y llega a miles de compradores.' },
  { icon: DollarSign, title: 'Cobra al instante', desc: 'Recibe pagos directo en tu billetera digital con proteccion total.' },
];

const BENEFITS = [
  { icon: Users, title: 'Miles de compradores', desc: 'Accede a una audiencia activa buscando lo que vendes.' },
  { icon: ShieldCheck, title: 'Compra protegida', desc: 'Sistema de escrow que protege a compradores y vendedores.' },
  { icon: Truck, title: 'Logistica integrada', desc: 'Heisenberg Engine conecta tus envios con repartidores verificados.' },
  { icon: Megaphone, title: 'Zona Libre Ads', desc: 'Promociona tus productos con nuestro sistema de publicidad interna.' },
  { icon: TrendingUp, title: 'Dashboard de ventas', desc: 'Metricas, ordenes y preguntas en un solo lugar.' },
  { icon: CreditCard, title: 'Multiples pagos', desc: 'Tarjeta, billetera, transferencia. Tu comprador elige como pagar.' },
];

const SellerLanding = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleCTA = () => {
    if (user) {
      navigate('/seller/products');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-orange-600 via-orange-500 to-amber-400 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-80 h-80 bg-white rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-10 w-60 h-60 bg-yellow-300 rounded-full blur-[100px]" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
            Vende en<br />
            <span className="text-yellow-200">Zona Libre</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/80 max-w-2xl mx-auto mb-10">
            Abre tu tienda online gratis, llega a miles de compradores y cobra al instante. Sin contratos, sin letra chica.
          </p>
          <Button
            onClick={handleCTA}
            size="lg"
            className="bg-white text-orange-600 hover:bg-gray-100 text-lg px-10 py-4 rounded-full shadow-xl font-bold"
            data-testid="seller-cta-hero"
          >
            <Zap className="w-5 h-5 mr-2" />
            {user ? 'Ir a mis productos' : 'Crear mi tienda gratis'}
          </Button>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Como funciona</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-orange-100 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-orange-600" />
                  </div>
                  <div className="text-sm font-bold text-orange-500 mb-1">Paso {i + 1}</div>
                  <h3 className="text-lg font-bold mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-500">{s.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-gray-50 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-12">Por que vender aqui</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                  <Icon className="w-8 h-8 text-orange-500 mb-3" />
                  <h3 className="font-bold text-lg mb-1">{b.title}</h3>
                  <p className="text-sm text-gray-500">{b.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 sm:py-20 text-center">
        <div className="max-w-2xl mx-auto px-4">
          <Store className="w-12 h-12 mx-auto text-orange-500 mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Listo para vender?</h2>
          <p className="text-gray-500 mb-8">Crea tu cuenta y publica tu primer producto hoy.</p>
          <Button
            onClick={handleCTA}
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-10 py-4 rounded-full"
            data-testid="seller-cta-bottom"
          >
            Empezar ahora <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default SellerLanding;
